import { KeyRound, Wallet } from "lucide-react";

import { ColocaNetworkIcon, ColocaTokenIcon } from "@/shared/components/data-display/ColocaCryptoIcon";
import { ColocaMoneyTransit } from "@/shared/components/motion/ColocaMoneyTransit";
import useTranslations from "@/shared/lang/useTranslations";

import { useCryptoSwapLabels } from "../../hooks/useCryptoSwapLabels";
import { chunkAddress } from "../../utils/cryptoSwapAddress";

/** The token as it sits in an outside wallet: its logo wearing the chain's. */
function TokenOnChain({ symbol, network }: { symbol: string; network: string }) {
  return (
    <span className="relative">
      <ColocaTokenIcon symbol={symbol} size={28} />
      <ColocaNetworkIcon
        network={network}
        size={13}
        className="absolute -right-1 -bottom-1 rounded-full ring-2 ring-muted"
      />
    </span>
  );
}

function shortAddress(address: string | null | undefined): string | null {
  if (!address) return null;
  const { head, tail } = chunkAddress(address);
  return tail ? `${head}…${tail}` : head;
}

export interface BuyTransitProps {
  /** The fiat paid, e.g. `COP`. */
  sendCurrency: string;
  /** The token bought, e.g. `USDT`. */
  receiveCurrency: string;
  network: string;
  address?: string | null;
  className?: string;
}

/** A purchase on its way: pesos leave the wallet, become the token at Coloca and travel the chain. */
export function BuyTransit({ sendCurrency, receiveCurrency, network, address, className }: BuyTransitProps) {
  const { t } = useTranslations("cryptoSwaps");
  const labels = useCryptoSwapLabels();
  const chain = labels.networkShort(network);
  const caption = shortAddress(address);
  return (
    <ColocaMoneyTransit
      className={className}
      label={t("transit_buy_aria", { send: sendCurrency, receive: receiveCurrency, network: chain })}
      from={{ icon: <Wallet className="size-5" />, label: t("transit_wallet", { currency: sendCurrency }) }}
      to={{
        icon: <TokenOnChain symbol={receiveCurrency} network={network} />,
        label: t("transit_your_wallet"),
        caption: caption ? <span className="font-mono">{caption}</span> : undefined,
      }}
      legs={[
        { rail: { kind: "fiat" }, asset: { kind: "fiat", currency: sendCurrency } },
        {
          rail: { kind: "chain", network, label: chain },
          asset: { kind: "token", symbol: receiveCurrency },
        },
      ]}
    />
  );
}

export interface SellTransitProps {
  /** The token sold, e.g. `USDT`. */
  sendCurrency: string;
  /** The fiat paid out, e.g. `COP`. */
  receiveCurrency: string;
  network: string;
  /** The Bre-B key the pesos are paid to. */
  payoutKey?: string | null;
  className?: string;
}

/** A sale on its way: the token arrives over the chain, becomes pesos at Coloca and is paid to the key. */
export function SellTransit({
  sendCurrency,
  receiveCurrency,
  network,
  payoutKey,
  className,
}: SellTransitProps) {
  const { t } = useTranslations("cryptoSwaps");
  const labels = useCryptoSwapLabels();
  const chain = labels.networkShort(network);
  return (
    <ColocaMoneyTransit
      className={className}
      label={t("transit_sell_aria", { send: sendCurrency, receive: receiveCurrency, network: chain })}
      from={{
        icon: <TokenOnChain symbol={sendCurrency} network={network} />,
        label: t("transit_your_wallet"),
        caption: sendCurrency,
      }}
      to={{
        icon: <KeyRound className="size-5" />,
        label: t("transit_breb_key"),
        caption: payoutKey ? <span className="font-mono">{payoutKey}</span> : undefined,
      }}
      legs={[
        {
          rail: { kind: "chain", network, label: chain },
          asset: { kind: "token", symbol: sendCurrency },
        },
        { rail: { kind: "fiat" }, asset: { kind: "fiat", currency: receiveCurrency } },
      ]}
    />
  );
}
